import React, { useState, useEffect, useRef } from 'react';
import '../styles/MusicPlayer.css';

const MUSIC_BASE = '/music';

const fmtTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const MusicPlayer = () => {
  const [tracks, setTracks] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const audioRef = useRef(null);

  useEffect(() => {
    fetch(`${MUSIC_BASE}/manifest.json`)
      .then(r => r.json())
      .then(setTracks)
      .catch(e => console.error('[MusicPlayer] manifest load failed', e));
  }, []);

  const filtered = tracks;
  const active = tracks[activeIdx];

  useEffect(() => {
    if (!audioRef.current) return;
    audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (!audioRef.current || !active) return;
    if (playing) {
      audioRef.current.play().catch(() => setPlaying(false));
    } else {
      audioRef.current.pause();
    }
  }, [playing, activeIdx, active]);

  const onTrackSelect = (track) => {
    const idx = tracks.findIndex(t => t.src === track.src);
    if (idx < 0) return;
    setActiveIdx(idx);
    setPlaying(true);
  };

  const next = () => {
    if (!filtered.length || !active) return;
    const fIdx = filtered.findIndex(t => t.src === active.src);
    const nextTrack = filtered[(fIdx + 1) % filtered.length];
    onTrackSelect(nextTrack);
  };

  const prev = () => {
    if (!filtered.length || !active) return;
    const fIdx = filtered.findIndex(t => t.src === active.src);
    const prevTrack = filtered[(fIdx - 1 + filtered.length) % filtered.length];
    onTrackSelect(prevTrack);
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = pct * duration;
  };

  if (!tracks.length) {
    return (
      <div className="music-player-loading">
        <p>LOADING TRANSMISSION...</p>
      </div>
    );
  }

  return (
    <div className="music-player">
      <div className="mp-grid">
        <div className="mp-now-playing">
          <div className="mp-art-wrap">
            <div className="mp-art-corner top-left" />
            <div className="mp-art-corner top-right" />
            <div className="mp-art-corner bottom-left" />
            <div className="mp-art-corner bottom-right" />
            {active && active.cover ? (
              <img src={`${MUSIC_BASE}/${active.cover}`} alt={active.title} className="mp-art" />
            ) : (
              <div className="mp-art mp-art-empty">NO SIGNAL</div>
            )}
            {playing && <div className="mp-art-pulse" />}
          </div>

          <div className="mp-meta">
            <div className="mp-track-idx">
              TRK {String(activeIdx + 1).padStart(2, '0')} / {String(tracks.length).padStart(2, '0')}
            </div>
            <div className="mp-track-title">{active ? active.title : '—'}</div>
            <div className="mp-track-artist">{active ? active.artist : '—'}</div>
          </div>

          <div className="mp-progress" onClick={seek}>
            <div
              className="mp-progress-fill"
              style={{ width: duration ? `${(progress / duration) * 100}%` : '0%' }}
            />
            <div className="mp-progress-tick" />
          </div>
          <div className="mp-time">
            <span>{fmtTime(progress)}</span>
            <span>{fmtTime(duration || (active ? active.duration : 0))}</span>
          </div>

          <div className="mp-controls">
            <button className="mp-btn" onClick={prev} title="Previous">◀◀</button>
            <button
              className="mp-btn mp-btn-play"
              onClick={() => setPlaying(p => !p)}
              title={playing ? 'Pause' : 'Play'}
            >
              {playing ? '❚❚' : '▶'}
            </button>
            <button className="mp-btn" onClick={next} title="Next">▶▶</button>
            <div className="mp-volume">
              <span className="mp-vol-label">VOL</span>
              <input
                type="range"
                min="0"
                max="100"
                value={volume * 100}
                onChange={(e) => setVolume(Number(e.target.value) / 100)}
              />
              <span className="mp-vol-value">{String(Math.round(volume * 100)).padStart(3, '0')}</span>
            </div>
          </div>
        </div>

        <div className="mp-list-wrap">
          <div className="mp-list-header">
            <span className="mp-col-num">##</span>
            <span className="mp-col-thumb-spacer" />
            <span className="mp-col-title">TRACK</span>
            <span className="mp-col-time">TIME</span>
          </div>

          <div className="mp-list">
            {filtered.map((track, i) => {
              const isActive = active && active.src === track.src;
              return (
                <button
                  key={track.src}
                  className={`mp-row ${isActive ? 'active' : ''}`}
                  onClick={() => onTrackSelect(track)}
                >
                  <span className="mp-col-num">
                    {isActive && playing ? '▶' : String(i + 1).padStart(2, '0')}
                  </span>
                  {track.cover && (
                    <img
                      src={`${MUSIC_BASE}/${track.cover}`}
                      alt=""
                      className="mp-row-thumb"
                    />
                  )}
                  <span className="mp-col-title">
                    <span className="mp-row-title">{track.title}</span>
                    <span className="mp-row-artist">{track.artist}</span>
                  </span>
                  <span className="mp-col-time">{fmtTime(track.duration)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {active && (
        <audio
          ref={audioRef}
          src={`${MUSIC_BASE}/${active.src}`}
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onEnded={next}
        />
      )}
    </div>
  );
};

export default MusicPlayer;
